<template>
  <div class="tabs-con">
    <div
      ref="scrollCon"
      class="tabs-outer-scroll-con"
      @DOMMouseScroll.prevent="onScroll"
      @mousewheel.prevent="onScroll"
    >
      <div class="close-all-tab-con">
        <iDropdown
          transfer
          @on-click="onTabsMenuClick">
          <iButton
            size="small"
            type="primary">
            {{ $t('layouts.main.tabsbar.options') }}
            <iIcon type="arrow-down-b"/>
          </iButton>
          <iDropdownMenu slot="list">
            <iDropdownItem
              name="clearAll">{{ $t('layouts.main.tabsbar.close_all') }}</iDropdownItem>
            <iDropdownItem
              name="clearOthers">{{ $t('layouts.main.tabsbar.close_other') }}</iDropdownItem>
          </iDropdownMenu>
        </iDropdown>
      </div>
      <div
        ref="scrollBody"
        :style="{left: tabBodyLeft + 'px'}"
        class="tabs-inner-scroll-body"
      >
        <transition-group name="tablist-moving-animation">
          <iTag
            v-for="tab in tabs"
            ref="tabs"
            :key="tab.key"
            :name="tab.key"
            :closable="!isLast"
            :color="isActive(tab) ? '#ff6b45' : 'default'"
            type="dot"
            class="tabsbar-tag"
            @click.native="goToTab(tab)"
            @on-close="closeTab(tab)"
          >
            <span
              v-if="tab.loading"
              key="tab-text"
              class="loading"/>
            <span
              v-else-if="tab.label"
              key="tab-text"
              v-text="tab.label"/>
            <span
              v-else-if="tab.i18n"
              key="tab-text">{{ $t(tab.i18n) }}</span>
          </iTag>
        </transition-group>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import fillParams from '@/utils/params';

export default {
  name: 'TabsBar',
  data() {
    return {
      tabBodyLeft: 0,
      refsTab: [],
    };
  },
  computed: {
    ...mapState('App/TabsBar', ['tabs']),
    currentTab() {
      const { matched, ...tab } = this.$route;
      const { path } = matched[matched.length - 1];
      const key = fillParams(path, tab.params);
      return { ...tab, key };
    },
    isLast() {
      return this.currentTab.name === 'dashboard' && this.tabs.length === 1;
    },
  },
  watch: {
    $route: 'moveToCurrentTag',
  },
  mounted() {
    this.moveToCurrentTag();
  },
  methods: {
    ...mapActions('App/TabsBar', ['removeTab', 'removeTabsOther', 'removeTabsAll']),
    isActive(tab) { return tab.key === this.currentTab.key; },
    goToTab(tab) {
      if (this.isActive(tab)) return;
      this.$router.push({ ...tab });
    },
    closeTab(tab) {
      if (this.isActive(tab)) {
        const index = this.tabs.findIndex(t => t.key === tab.key);
        const nextTab = (index < this.tabs.length - 1) ?
          this.tabs[index + 1] :
          this.tabs[index - 1];
        if (nextTab) this.$router.push({ ...nextTab });
        else this.$router.push('/');
      } else {
        this.moveToCurrentTag();
      }
      this.removeTab(tab);
    },
    closeTabsOther(tab) {
      this.removeTabsOther(tab);
      this.goToTab(tab);
      this.moveToCurrentTag();
    },
    closeTabsAll() {
      this.removeTabsAll();
      this.$router.push('/');
    },
    onTabsMenuClick(type) {
      if (type === 'clearAll') {
        this.closeTabsAll();
      } else if (type === 'clearOthers') {
        this.closeTabsOther(this.currentTab);
      }
      this.tabBodyLeft = 0;
    },
    moveToCurrentTag() {
      this.$nextTick(() => {
        const { tabs } = this.$refs;
        if (!tabs) return;
        const tab = tabs.find(t => t.key === this.currentTab.key);
        if (tab) this.moveToTarget(tab.$el);
      });
    },
    moveToTarget(el) {
      if (el || !el) return;
      if (el.offsetLeft < -this.elBodyLeft) {
        // The label is on the left side of the visible area
        this.elBodyLeft = -el.offsetLeft + 10;
      } else if (
        el.offsetLeft + 10 > -this.tabBodyLeft &&
        el.offsetLeft + el.offsetWidth <
          -(this.tabBodyLeft + this.$refs.scrollCon.offsetWidth) - 100
      ) {
        // Label in the visible area
        this.tabBodyLeft = Math.min(
          0,
          this.$refs.scrollCon.offsetWidth - 100 - el.offsetWidth - el.offsetLeft - 20,
        );
      } else {
        // The label is to the right of the visible area
        this.tabBodyLeft = -(
          (el.offsetLeft - this.$refs.scrollCon.offsetWidth - 100 - el.offsetWidth) + 20
        );
      }
    },
    onScroll(event) {
      const { scrollCon, scrollBody } = this.$refs;
      let delta = 0;
      if (event.type === 'DOMMouseScroll' || event.type === 'mousewheel') {
        delta = event.wheelDelta ? event.wheelDelta : -(event.detail || 0) * 40;
      }
      if (delta > 0) {
        this.tabBodyLeft = Math.min(0, this.tabBodyLeft + delta);
      } else if (scrollCon.offsetWidth - 100 < scrollBody.offsetWidth) {
        if (this.tabBodyLeft > -((scrollBody.offsetWidth - scrollCon.offsetWidth) + 100)) {
          this.tabBodyLeft = Math.max(
            this.tabBodyLeft + delta,
            scrollCon.offsetWidth - scrollBody.offsetWidth - 100,
          );
        }
      } else {
        this.tabBodyLeft = 0;
      }
    },
  },
};
</script>

<style>
.tabsbar-tag .ivu-tag-text {
  display: inline-block;
  min-width: 60px;
}
</style>

<style scoped>
.tabs-con {
  height: 40px;
  overflow: hidden;
}
.tabs-outer-scroll-con {
  position: relative;
  box-sizing: border-box;
  padding-right: 120px;
  width: 100%;
  height: 100%;
}
.tabs-inner-scroll-body {
  position: absolute;
  padding: 2px 10px;
  overflow: visible;
  white-space: nowrap;
  transition: left 0.3s ease;
}
.close-all-tab-con {
  position: absolute;
  right: 0;
  top: 0;
  box-sizing: border-box;
  padding-top: 8px;
  text-align: center;
  width: 110px;
  height: 100%;
  background: white;
  box-shadow: -3px 0 15px 3px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.loading:after {
  content: ' .';
  animation: dots 1s steps(5, end) infinite;
}

@keyframes dots {
  0%, 20% {
    color: rgba(0,0,0,0);
    text-shadow:
      .25em 0 0 rgba(0,0,0,0),
      .5em 0 0 rgba(0,0,0,0);
  }
  40% {
    color: white;
    text-shadow:
      .25em 0 0 rgba(0,0,0,0),
      .5em 0 0 rgba(0,0,0,0);
  }
  60% {
    text-shadow:
      .25em 0 0 white,
      .5em 0 0 rgba(0,0,0,0);
  }
  80%, 100% {
    text-shadow:
      .25em 0 0 white,
      .5em 0 0 white;
  }
}
</style>
